import PropTypes from 'prop-types';
import {
  Typography,
  ButtonBase,
  FormHelperText,
  makeStyles,
} from '@material-ui/core';
import Dropzone from 'react-dropzone';
import { isObject } from 'formik';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import ActionButton from '../actionButton';
import { ReactComponent as PlaceholderIcon } from '../../../assets/icons/upload.svg';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';
import { ellipsis } from '../../../constants/helperCssRules';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import { isArrayEmpty } from '../../../utility/helpers';
import { validateFileType } from '../../../utility/validation';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  main: {
    width: '100%',
    display: 'grid',
  },
  content: {
    display: 'grid',
    height: 40,
    alignItems: 'center',
  },
  contentButton: {
    position: 'relative',
    top: spacing(0.5),
  },
  placeholderContainer: {
    boxSizing: 'border-box',
    cursor: 'pointer',
    border: `1px solid ${primary.bluish1}`,
    borderRadius: 44,
    display: 'flex',
    alignItems: 'center',
    padding: spacing(0, 4),
    height: 40,
    userSelect: 'none',
  },
  placeholder: {
    marginLeft: spacing(2),
  },
  uploadedFile: {
    marginTop: spacing(2),
    ...ellipsis(),
  },
  uploadedFilesWrapper: {
    maxWidth: 'calc(100% - 90px)',
  },
  uploadedFileWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  urlLink: {
    maxWidth: 364,
  },
  uploadButton: {
    display: 'flex',
    width: 'fit-content',
  },
  placeholderIcon: {
    width: 18,
    height: 18,
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    wordBreak: 'break-word',
  },
  labelContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
}));

const AddFileField = ({
  uploadedFiles = [] || null,
  customUrlLinkClass = null,
  placeholder,
  errorMessage,
  hasError,
  onAddFile,
  onRejectFile = null,
  isMultiple = true,
  label = '',
  types,
}) => {
  const classes = useStyles();
  const hasFile = isMultiple ? !isArrayEmpty(uploadedFiles) : uploadedFiles;

  const onAcceptFile = ([file]) => onAddFile(file);

  const handleRejectFile = () => {
    if (onRejectFile) {
      onRejectFile();
    }
  };

  const onDeleteFile = e => {
    e.stopPropagation();
    onAddFile(null);
  };

  const getFileName = () => {
    const fileUrl = isObject(uploadedFiles)
      ? uploadedFiles.name
      : uploadedFiles;
    if (fileUrl && fileUrl.includes('http')) {
      const match = fileUrl.match(/\/([^/]+\.pdf)/);
      return match ? match[1] : fileUrl;
    }
    return fileUrl;
  };

  const renderFile = () => {
    return (
      <div className={classes.uploadedFileWrapper}>
        <Link
          className={classNames(classes.urlLink, customUrlLinkClass)}
          to={
            isObject(uploadedFiles)
              ? URL.createObjectURL(uploadedFiles)
              : uploadedFiles
          }
          target="_blank"
        >
          <TextBoxWithTooltip variant="subtitle1" text={getFileName()} />
        </Link>
        <ActionButton
          type={ACTION_BUTTON_TYPES.DELETE}
          onClickHandler={onDeleteFile}
        />
      </div>
    );
  };

  const renderPlaceholder = () => (
    <ButtonBase className={classes.placeholderContainer} component="div">
      <PlaceholderIcon className={classes.placeholderIcon} />
      <Typography variant="subtitle1" className={classes.placeholder}>
        {placeholder}
      </Typography>
    </ButtonBase>
  );

  return (
    <>
      {label && (
        <div className={classes.labelContainer}>
          <FormHelperText className={classes.label}>{label}</FormHelperText>
        </div>
      )}
      <div className={classes.main}>
        <div className={classes.content}>
          {!isMultiple &&
          hasFile &&
          validateFileType(uploadedFiles, Object.keys(types)) ? (
            renderFile()
          ) : (
            <div className={classes.uploadButton}>
              <div className={classes.contentButton}>
                <Dropzone
                  onDropAccepted={onAcceptFile}
                  onDropRejected={handleRejectFile}
                  accept={types}
                  multiple={isMultiple}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps()}>
                      <input {...getInputProps()} />
                      {renderPlaceholder()}
                    </div>
                  )}
                </Dropzone>
              </div>
            </div>
          )}
          {isMultiple && hasFile && (
            <div className={classes.uploadedFilesWrapper}>
              {uploadedFiles?.map((item, index) => {
                return (
                  <div
                    key={`file_${index}`}
                    className={classes.uploadedFileWrapper}
                  >
                    <TextBoxWithTooltip
                      className={classes.uploadedFile}
                      variant="subtitle1"
                      text={item.name}
                    />
                    <ActionButton
                      type={ACTION_BUTTON_TYPES.DELETE}
                      onClickHandler={onDeleteFile}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {hasError && errorMessage && (
          <FormHelperText error={hasError}>{errorMessage}</FormHelperText>
        )}
      </div>
    </>
  );
};

AddFileField.propTypes = {
  placeholder: PropTypes.string.isRequired,
  types: PropTypes.object.isRequired,
  isMultiple: PropTypes.bool,
  uploadedFiles: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    ),
    PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  ]),
  onAddFile: PropTypes.func.isRequired,
  onRejectFile: PropTypes.func,
  label: PropTypes.string,
  customUrlLinkClass: PropTypes.string,
};

export default AddFileField;
